import {
  HandlebarTemplate,
  formatAmountWithCurrency,
  localeToCurrencyCode,
  usePromotionLayoutsContext,
} from '@storefront/lib-global';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { LastOfferedTooltip } from '@storefront/lib-global/globalComponents/tooltip/LastOfferedTooltip';
import { PriceTooltip } from '@storefront/lib-global/globalComponents/tooltip/PriceTooltip';
import { Locale } from '@storefront/lib-global/utils/internationalization/localeHelper';
import { createPromotionHandlebarData } from '@storefront/lib-global/utils/transformers';
import { useSizeAndWidthContext } from '../../../context';
import { useProductPrice } from '../../../hooks';
import type { ProductPriceProps } from '../../../types';
import { ActualPriceComponent, ThenPriceComponent, WasPriceComponent } from './PriceComponents';
import { ActualPrice } from './PriceComponents.styles';
import { PriceRange, Prices, PricesContainer } from './ProductPrice.styles';

export function ProductPrice({ tooltipContent, onlyActualPrice, promotion }: ProductPriceProps) {
  const { locale } = useLocaleContext();
  const { promotionLayouts } = usePromotionLayoutsContext();
  const { selectedSize, selectedWidth, variants, priceRanges } = useSizeAndWidthContext();
  const { actualPrice, thenPrice, wasPrice, toDate, fromDate, lastOfferedAtDate } = useProductPrice({
    selectedSize,
    selectedWidth,
    variants,
  });
  const isLocaleAvailable = [Locale.EN_GB, Locale.EN_IE, Locale.STOREFRONT_OUTLET_LOCALE].includes(locale as string);
  const isNotNorthAmericanLocale = ![Locale.EN_US, Locale.EN_CA, Locale.FR_CA].includes(locale as string);
  const isWasPrice = Boolean(wasPrice && wasPrice !== actualPrice);
  const isThenPrice = Boolean(isLocaleAvailable && thenPrice && thenPrice !== actualPrice);
  const { now, then, was } = priceRanges ?? {};
  const isNotMarkdown = Boolean(
    now?.maxPrice &&
      was?.maxPrice &&
      now?.minPrice &&
      was?.minPrice &&
      now?.maxPrice === was?.maxPrice &&
      now?.minPrice === was?.minPrice,
  );
  const nowHasPriceRange = Boolean(now && now.minPrice !== now.maxPrice);
  const isThenDifferentFromWasPrice = Boolean(then?.minPrice && was?.minPrice && then.minPrice !== was.minPrice);
  const isMinWasPrice = Boolean(was?.minPrice && !isNotMarkdown);
  const isMinThenPrice = Boolean(isLocaleAvailable && then?.minPrice && now?.minPrice && then.minPrice > now.minPrice);
  const currencySymbol = localeToCurrencyCode[locale];
  const formattedWasPrice = formatAmountWithCurrency(was?.minPrice, currencySymbol);
  const formattedThenPrice = formatAmountWithCurrency(then?.minPrice, currencySymbol);
  const formattedNowMinPrice = formatAmountWithCurrency(now?.minPrice, currencySymbol);
  const formattedNowMaxPrice = formatAmountWithCurrency(now?.maxPrice, currencySymbol);

  const promotionHandlebarData = createPromotionHandlebarData({ promotionLayouts, promotion });

  if (onlyActualPrice) {
    return <Prices>{actualPrice && <ActualPrice data-testid="actualPrice">{actualPrice}</ActualPrice>}</Prices>;
  }

  return (
    <PricesContainer>
      <Prices
        $pricesStrikethrough={!!promotionHandlebarData && promotion?.pricesStrikethrough}
        $hasPromotion={!!promotionHandlebarData?.input}
      >
        {isNotNorthAmericanLocale && selectedSize === '' ? (
          <>
            {isMinWasPrice && <WasPriceComponent price={formattedWasPrice} />}
            {isThenDifferentFromWasPrice && isMinThenPrice && <ThenPriceComponent price={formattedThenPrice} />}
            {nowHasPriceRange && (
              <PriceRange
                data-lowest-price={formattedNowMinPrice}
                data-highest-price={formattedNowMaxPrice}
              >{`${formattedNowMinPrice} - ${formattedNowMaxPrice}`}</PriceRange>
            )}
            {!nowHasPriceRange && actualPrice && (
              <ActualPriceComponent price={actualPrice} isDiscounted={isWasPrice || isThenPrice} />
            )}
          </>
        ) : (
          <>
            {isWasPrice && <WasPriceComponent price={wasPrice} />}
            {isThenPrice && <ThenPriceComponent price={thenPrice} />}
            {actualPrice && <ActualPriceComponent price={actualPrice} isDiscounted={isWasPrice || isThenPrice} />}
          </>
        )}
        <LastOfferedTooltip
          fromDate={fromDate}
          toDate={toDate}
          wasPrice={wasPrice}
          lastOfferedAtDate={lastOfferedAtDate}
        />
        {tooltipContent && !!wasPrice && <PriceTooltip {...tooltipContent} />}
      </Prices>
      {promotionHandlebarData && (
        <HandlebarTemplate template={promotionHandlebarData.template} input={promotionHandlebarData.input} />
      )}
    </PricesContainer>
  );
}

export default ProductPrice;
